import React, {useEffect, useState} from 'react';
import {Layout, Tabs, Divider, Switch} from "antd";
import {useToast} from "@chakra-ui/react";
import Headerbar from "../../navBars/Headerbar";
import Sidebar from "../../navBars/Sidebar";
import {MDBCol, MDBRow} from "mdbreact";
import {Button, Input} from "@nextui-org/react";
import {doc, getDoc, getFirestore, setDoc, updateDoc} from "firebase/firestore";
import Firebase from "../../Firebase/Firebase";
import {Tag} from "primereact/tag";
import {getAuth, onAuthStateChanged} from "firebase/auth";
import {WithdrawalFeesEditor} from "./WithdrawalFeesEditor";


const db   = getFirestore(Firebase);
const auth = getAuth(Firebase);
const {Content} = Layout;
const {TabPane} = Tabs;

// ─── Shared helpers ────────────────────────────────────────────────────────────

const Section = ({title, sub, children}: {title: string; sub?: string; children: React.ReactNode}) => (
    <div style={{background:'#fff', borderRadius:12, border:'1px solid #eee',
                 boxShadow:'0 1px 4px rgba(0,0,0,.06)', padding:'18px 20px', marginBottom:16}}>
        <div style={{fontWeight:700, fontSize:14, color:'#29327b', marginBottom:sub ? 2 : 12}}>{title}</div>
        {sub && <div style={{fontSize:12, color:'#888', marginBottom:12}}>{sub}</div>}
        {children}
    </div>
);

const FieldRow = ({label, sub, children}: {label: string; sub?: string; children: React.ReactNode}) => (
    <div style={{display:'flex', justifyContent:'space-between', alignItems:'flex-start',
                 padding:'10px 0', borderBottom:'1px solid #f5f5f5', gap:16}}>
        <div style={{flex:'0 0 220px'}}>
            <div style={{fontSize:13, fontWeight:500, color:'#333'}}>{label}</div>
            {sub && <div style={{fontSize:11, color:'#aaa', marginTop:2}}>{sub}</div>}
        </div>
        <div style={{flex:1, display:'flex', justifyContent:'flex-end', alignItems:'center'}}>{children}</div>
    </div>
);

// ─── Settings page ─────────────────────────────────────────────────────────────

const Settings = () => {
    const toast = useToast();
    const [collapsed, setCollapsed] = useState(false);
    const [saving, setSaving]       = useState(false);

    // paymentConfig/global
    const [payConfig, setPayConfig] = useState<any>({});
    // appSettings/global
    const [appSettings, setAppSettings] = useState<any>({});
    // appSettings/notifications
    const [notifSettings, setNotifSettings] = useState<any>({});
    // appSettings/support (public read: the apps and the Contact page use it)
    const [supportSettings, setSupportSettings] = useState<any>({});

    useEffect(() => {
        const unsub = onAuthStateChanged(auth, (user) => {
            if (!user) return;
            getDoc(doc(db, 'paymentConfig', 'global')).then(snap => {
                if (snap.exists()) setPayConfig(snap.data());
            });
            getDoc(doc(db, 'appSettings', 'global')).then(snap => {
                if (snap.exists()) setAppSettings(snap.data());
            });
            getDoc(doc(db, 'appSettings', 'notifications')).then(snap => {
                if (snap.exists()) setNotifSettings(snap.data());
            });
            getDoc(doc(db, 'appSettings', 'support')).then(snap => {
                if (snap.exists()) setSupportSettings(snap.data());
            });
        });
        return () => unsub();
    }, []);

    const savePayConfig = async (updates: any) => {
        setSaving(true);
        try {
            await setDoc(doc(db, 'paymentConfig', 'global'), {...payConfig, ...updates}, {merge: true});
            setPayConfig((p: any) => ({...p, ...updates}));
            toast({position:'top', title:'Payment config saved', status:'success', duration:2000, isClosable:true});
        } catch (e: any) {
            toast({position:'top', title: e.message, status:'error', duration:3000, isClosable:true});
        } finally { setSaving(false); }
    };

    const saveAppSettings = async (updates: any) => {
        setSaving(true);
        try {
            await setDoc(doc(db, 'appSettings', 'global'), {...appSettings, ...updates}, {merge: true});
            setAppSettings((p: any) => ({...p, ...updates}));
            toast({position:'top', title:'App settings saved', status:'success', duration:2000, isClosable:true});
        } catch (e: any) {
            toast({position:'top', title: e.message, status:'error', duration:3000, isClosable:true});
        } finally { setSaving(false); }
    };

    const saveSupportSettings = async (updates: any) => {
        setSaving(true);
        try {
            await setDoc(doc(db, 'appSettings', 'support'), updates, {merge: true});
            setSupportSettings((p: any) => ({...p, ...updates}));
            toast({position:'top', title:'Support settings saved', status:'success', duration:2000, isClosable:true});
        } catch (e: any) {
            toast({position:'top', title: e.message, status:'error', duration:3000, isClosable:true});
        } finally { setSaving(false); }
    };

    const saveNotifSettings = async (updates: any) => {
        setSaving(true);
        try {
            await setDoc(doc(db, 'appSettings', 'notifications'), {...notifSettings, ...updates}, {merge: true});
            setNotifSettings((p: any) => ({...p, ...updates}));
            toast({position:'top', title:'Notification settings saved', status:'success', duration:2000, isClosable:true});
        } catch (e: any) {
            toast({position:'top', title: e.message, status:'error', duration:3000, isClosable:true});
        } finally { setSaving(false); }
    };

    // Inline number editor
    const NumField = ({value, onSave, prefix = '', suffix = ''}: {value: number; onSave: (v: number) => void; prefix?: string; suffix?: string}) => {
        const [v, setV] = useState(String(value ?? ''));
        useEffect(() => setV(String(value ?? '')), [value]);
        return (
            <div style={{display:'flex', alignItems:'center', gap:8}}>
                {prefix && <span style={{fontSize:12, color:'#888'}}>{prefix}</span>}
                <input
                    type="number" value={v}
                    onChange={e => setV(e.target.value)}
                    style={{width:90, padding:'5px 8px', borderRadius:8, border:'1px solid #eee',
                            fontSize:13, textAlign:'right'}}
                />
                {suffix && <span style={{fontSize:12, color:'#888'}}>{suffix}</span>}
                <button style={{fontSize:11, color:'#29327b', border:'1px solid #c7d2fe',
                                borderRadius:6, padding:'3px 10px', cursor:'pointer', background:'#eef2ff'}}
                        onClick={() => onSave(parseFloat(v))}>
                    Save
                </button>
            </div>
        );
    };

    const StrField = ({value, onSave, placeholder = ''}: {value: string; onSave: (v: string) => void; placeholder?: string}) => {
        const [v, setV] = useState(value ?? '');
        useEffect(() => setV(value ?? ''), [value]);
        return (
            <div style={{display:'flex', alignItems:'center', gap:8}}>
                <input
                    type="text" value={v} placeholder={placeholder}
                    onChange={e => setV(e.target.value)}
                    style={{width:200, padding:'5px 8px', borderRadius:8, border:'1px solid #eee', fontSize:13}}
                />
                <button style={{fontSize:11, color:'#29327b', border:'1px solid #c7d2fe',
                                borderRadius:6, padding:'3px 10px', cursor:'pointer', background:'#eef2ff'}}
                        onClick={() => onSave(v)}>
                    Save
                </button>
            </div>
        );
    };

    const rates = payConfig.serviceChargeRates ?? {};
    // Refund fee rates live nested at providers.<p>.methods.<m>.refund_fee_rate
    // in the real document (not a flat top-level refundFeeRates field) —
    // getPaymentConfig() in Cloud Functions prefers that nested shape
    // whenever it's present, which it always is in production.
    const provs = payConfig.providers ?? {};
    const saveProviderMethodField = (provider: string, method: string, field: string, value: any) => {
        const providerBlock = provs[provider] ?? {};
        const methodBlock = providerBlock.methods?.[method] ?? {};
        savePayConfig({
            providers: {
                ...provs,
                [provider]: {
                    ...providerBlock,
                    methods: {
                        ...(providerBlock.methods ?? {}),
                        [method]: {...methodBlock, [field]: value},
                    },
                },
            },
        });
    };
    const bankTransferRFR = provs?.paychangu?.methods?.bank_transfer?.refund_fee_rate;
    const bankTransferRFRObj = typeof bankTransferRFR === 'object' && bankTransferRFR !== null ? bankTransferRFR : {};
    const saveBankTransferRefundFeeField = (field: 'rate' | 'flatFee', value: number) => {
        saveProviderMethodField('paychangu', 'bank_transfer', 'refund_fee_rate', {...bankTransferRFRObj, [field]: value});
    };

    return (
        <Layout style={{minHeight:'100vh'}}>
            <Headerbar collapsed={collapsed} setCollapsed={setCollapsed}/>
            <Layout className="site-layout">
                <Sidebar collapsed={collapsed}/>
                <Content>
                    <div style={{padding:'20px 24px'}}>
                        <div className="page-header" style={{marginBottom:16}}>
                            <h4 className="page-title">Settings</h4>
                            <span style={{fontSize:12, color:'#888'}}>
                                Manage payment config, app behaviour, and notifications
                            </span>
                        </div>

                        <Tabs defaultActiveKey="payment" size="large">

                            {/* ── Payment & Fees ── */}
                            <TabPane tab="Payment & Fees" key="payment">
                                <MDBRow>
                                    <MDBCol md="6">
                                        <Section title="Service Charge Rates"
                                                 sub="Commission deducted from seller payouts. e.g. 0.04 = 4%">
                                            <FieldRow label="PayChangu rate"
                                                      sub="Applied to all PayChangu payouts">
                                                <NumField
                                                    value={rates.paychangu ?? 0.04}
                                                    suffix="(e.g. 0.04 = 4%)"
                                                    onSave={v => savePayConfig({
                                                        serviceChargeRates: {...rates, paychangu: v}
                                                    })}
                                                />
                                            </FieldRow>
                                            <FieldRow label="PawaPay rate"
                                                      sub="Applied to all PawaPay payouts">
                                                <NumField
                                                    value={rates.pawapay ?? 0.0376}
                                                    suffix="(e.g. 0.0376 = 3.76%)"
                                                    onSave={v => savePayConfig({
                                                        serviceChargeRates: {...rates, pawapay: v}
                                                    })}
                                                />
                                            </FieldRow>
                                        </Section>

                                        <Section title="Refund Fee Rates"
                                                 sub="Fees deducted from refund amount when buyer is refunded">
                                            <FieldRow label="PayChangu MoMo" sub="Mobile money refund fee">
                                                <NumField
                                                    value={provs?.paychangu?.methods?.momo?.refund_fee_rate ?? 0.0333}
                                                    onSave={v => saveProviderMethodField('paychangu', 'momo', 'refund_fee_rate', v)}
                                                />
                                            </FieldRow>
                                            <FieldRow label="PayChangu Bank Transfer — rate"
                                                      sub="Percentage component. PayChangu bills a bank-transfer refund as an ordinary disbursement (~1.5% + a flat fee) — same pricing as a seller bank payout.">
                                                <NumField
                                                    value={bankTransferRFRObj.rate ?? 0.015}
                                                    onSave={v => saveBankTransferRefundFeeField('rate', v)}
                                                />
                                            </FieldRow>
                                            <FieldRow label="PayChangu Bank Transfer — flat fee"
                                                      sub="Flat MWK component. PayChangu advertises MWK 700; observed running closer to MWK 950 in practice.">
                                                <NumField
                                                    value={bankTransferRFRObj.flatFee ?? 950}
                                                    prefix="MWK"
                                                    onSave={v => saveBankTransferRefundFeeField('flatFee', v)}
                                                />
                                            </FieldRow>
                                            <FieldRow label="PawaPay MoMo" sub="PawaPay refund fee">
                                                <NumField
                                                    value={provs?.pawapay?.methods?.momo?.refund_fee_rate ?? 0.028}
                                                    onSave={v => saveProviderMethodField('pawapay', 'momo', 'refund_fee_rate', v)}
                                                />
                                            </FieldRow>
                                        </Section>
                                    </MDBCol>

                                    <MDBCol md="6">
                                        <Section title="Active Payment Provider"
                                                 sub="Which gateway handles new buyer payments">
                                            <FieldRow label="Provider">
                                                <div style={{display:'flex', gap:10}}>
                                                    {['paychangu', 'pawapay'].map(p => (
                                                        <button key={p}
                                                            onClick={() => savePayConfig({activePaymentProvider: p})}
                                                            style={{
                                                                fontSize:12, padding:'5px 16px', borderRadius:8, cursor:'pointer',
                                                                border: (payConfig.activePaymentProvider ?? 'paychangu') === p
                                                                    ? '2px solid #29327b' : '1px solid #eee',
                                                                background: (payConfig.activePaymentProvider ?? 'paychangu') === p
                                                                    ? '#eef2ff' : '#fff',
                                                                color: (payConfig.activePaymentProvider ?? 'paychangu') === p
                                                                    ? '#29327b' : '#555',
                                                                fontWeight: (payConfig.activePaymentProvider ?? 'paychangu') === p ? 700 : 400,
                                                            }}>
                                                            {p}
                                                        </button>
                                                    ))}
                                                </div>
                                            </FieldRow>
                                        </Section>

                                        <Section title="Min Order Amount"
                                                 sub="Minimum checkout amount in MWK. 0 = no minimum">
                                            <FieldRow label="Minimum (MWK)">
                                                <NumField
                                                    value={payConfig.minOrderAmount ?? 0}
                                                    prefix="MWK"
                                                    onSave={v => savePayConfig({minOrderAmount: v})}
                                                />
                                            </FieldRow>
                                        </Section>

                                        <Section title="Bank Transfer Minimums"
                                                 sub="Bank transfer carries a large flat fee (see Refund Fee Rates), so it's gated by amount on both sides.">
                                            <FieldRow label="Min order to offer at checkout"
                                                      sub="Below this, buyers only see Mobile Money">
                                                <NumField
                                                    value={payConfig.bankTransferMinOrderAmount ?? 2000}
                                                    prefix="MWK"
                                                    onSave={v => savePayConfig({bankTransferMinOrderAmount: v})}
                                                />
                                            </FieldRow>
                                            <FieldRow label="Free bank payout from"
                                                      sub="At/above this, bank sellers are paid free the next day. Below it, money waits — the seller can cash out by bank and pay the bank fee, or use Mobile Money (free)">
                                                <NumField
                                                    value={payConfig.bankTransferMinPayoutAmount ?? 88000}
                                                    prefix="MWK"
                                                    onSave={v => savePayConfig({bankTransferMinPayoutAmount: v})}
                                                />
                                            </FieldRow>
                                        </Section>

                                        <Section title="Support"
                                                 sub="Shown to users in Help & Support and on the website's Contact page.">
                                            <FieldRow label="WhatsApp number"
                                                      sub="International format, e.g. 265991234567. Empty hides the WhatsApp button.">
                                                <StrField
                                                    value={supportSettings.whatsappNumber ?? ''}
                                                    placeholder="265991234567"
                                                    onSave={v => saveSupportSettings({whatsappNumber: v.replace(/[^\d]/g, '')})}
                                                />
                                            </FieldRow>
                                        </Section>

                                        <Section title="Mobile Money Withdrawal Fees"
                                                 sub="What Airtel / TNM charge a seller to cash out at an agent. Shopped never charges this — the product price helper uses it to show sellers the cost and offer to include it in their price. Update whenever the operators change their fees.">
                                            <WithdrawalFeesEditor
                                                value={payConfig.withdrawalFees}
                                                onSave={(withdrawalFees) => savePayConfig({withdrawalFees})}
                                            />
                                        </Section>

                                        <Section title="Firebase Remote Config"
                                                 sub="Some settings (maintenance mode, promotion pricing, app version) are managed via Firebase Remote Config.">
                                            <a
                                                href="https://console.firebase.google.com/project/shoppedmw-app/remoteconfig"
                                                target="_blank" rel="noreferrer"
                                                style={{
                                                    display:'inline-flex', alignItems:'center', gap:8,
                                                    fontSize:13, color:'#29327b', fontWeight:600,
                                                    padding:'8px 16px', borderRadius:8,
                                                    background:'#eef2ff', border:'1px solid #c7d2fe',
                                                    textDecoration:'none', marginTop:8,
                                                }}>
                                                Open Remote Config Console →
                                            </a>
                                            <div style={{fontSize:12, color:'#888', marginTop:8}}>
                                                Editable there: maintenance_mode · flash_sale · global_discount ·
                                                promotion_pricing · promo_weight_tiers · otp_at_enabled ·
                                                product_min/max_price · app version
                                            </div>
                                        </Section>
                                    </MDBCol>
                                </MDBRow>
                            </TabPane>

                            {/* ── App Control ── */}
                            <TabPane tab="App Control" key="app">
                                <MDBRow>
                                    <MDBCol md="6">
                                        <Section title="Maintenance Mode"
                                                 sub="Shows a full-screen maintenance page to all app users">
                                            <FieldRow label="Maintenance mode"
                                                      sub="Blocks all app navigation when enabled">
                                                <Switch
                                                    checked={!!appSettings.maintenance_mode}
                                                    onChange={v => saveAppSettings({maintenance_mode: v})}
                                                    checkedChildren="ON"
                                                    unCheckedChildren="OFF"
                                                />
                                            </FieldRow>
                                            {appSettings.maintenance_mode && (
                                                <div style={{background:'#fef2f2', borderRadius:8, padding:'10px 12px',
                                                             fontSize:12, color:'#991b1b', marginTop:8}}>
                                                    ⚠ Maintenance mode is active — app is blocked for all users
                                                </div>
                                            )}
                                        </Section>

                                        <Section title="Announcement Bar"
                                                 sub="Pinned banner shown at the top of the home screen">
                                            <FieldRow label="Enabled">
                                                <Switch
                                                    checked={!!appSettings.announcement_enabled}
                                                    onChange={v => saveAppSettings({announcement_enabled: v})}
                                                />
                                            </FieldRow>
                                            <FieldRow label="Message text">
                                                <StrField
                                                    value={appSettings.announcement_text ?? ''}
                                                    placeholder="e.g. Free delivery this weekend!"
                                                    onSave={v => saveAppSettings({announcement_text: v})}
                                                />
                                            </FieldRow>
                                            <FieldRow label="Background colour" sub="Hex colour e.g. #016DD1">
                                                <div style={{display:'flex', alignItems:'center', gap:10}}>
                                                    <div style={{
                                                        width:28, height:28, borderRadius:6,
                                                        background: appSettings.announcement_bg_color ?? '#016DD1',
                                                        border:'1px solid #eee',
                                                    }}/>
                                                    <StrField
                                                        value={appSettings.announcement_bg_color ?? '#016DD1'}
                                                        placeholder="#016DD1"
                                                        onSave={v => saveAppSettings({announcement_bg_color: v})}
                                                    />
                                                </div>
                                            </FieldRow>
                                        </Section>
                                    </MDBCol>

                                    <MDBCol md="6">
                                        <Section title="Flash Sale"
                                                 sub="Sitewide flash sale banner with optional time window">
                                            <FieldRow label="Enabled">
                                                <Switch
                                                    checked={!!appSettings.flash_sale_enabled}
                                                    onChange={v => saveAppSettings({flash_sale_enabled: v})}
                                                />
                                            </FieldRow>
                                            <FieldRow label="Title">
                                                <StrField
                                                    value={appSettings.flash_sale_title ?? 'Flash Sale!'}
                                                    onSave={v => saveAppSettings({flash_sale_title: v})}
                                                />
                                            </FieldRow>
                                            <FieldRow label="Start time" sub="ISO 8601 e.g. 2026-06-01T00:00:00Z">
                                                <StrField
                                                    value={appSettings.flash_sale_start_time ?? ''}
                                                    placeholder="2026-06-01T00:00:00Z"
                                                    onSave={v => saveAppSettings({flash_sale_start_time: v})}
                                                />
                                            </FieldRow>
                                            <FieldRow label="End time">
                                                <StrField
                                                    value={appSettings.flash_sale_end_time ?? ''}
                                                    placeholder="2026-06-07T23:59:59Z"
                                                    onSave={v => saveAppSettings({flash_sale_end_time: v})}
                                                />
                                            </FieldRow>
                                        </Section>

                                        <Section title="Global Discount"
                                                 sub="Sitewide percentage discount applied to all products">
                                            <FieldRow label="Enabled">
                                                <Switch
                                                    checked={!!appSettings.global_discount_enabled}
                                                    onChange={v => saveAppSettings({global_discount_enabled: v})}
                                                />
                                            </FieldRow>
                                            <FieldRow label="Discount %" sub="0.0 = off">
                                                <NumField
                                                    value={appSettings.global_discount_percent ?? 0}
                                                    suffix="%"
                                                    onSave={v => saveAppSettings({global_discount_percent: v})}
                                                />
                                            </FieldRow>
                                            <FieldRow label="Label" sub="e.g. Summer Sale">
                                                <StrField
                                                    value={appSettings.global_discount_label ?? ''}
                                                    placeholder="Summer Sale"
                                                    onSave={v => saveAppSettings({global_discount_label: v})}
                                                />
                                            </FieldRow>
                                        </Section>
                                    </MDBCol>
                                </MDBRow>
                            </TabPane>

                            {/* ── Feature Flags ── */}
                            <TabPane tab="Features" key="features">
                                <MDBRow>
                                    <MDBCol md="6">
                                        <Section title="App Features"
                                                 sub="Toggle individual features on/off for all users">
                                            {[
                                                {key:'chat_enabled',            label:'In-app Chat',         sub:'Buyer ↔ seller messaging'},
                                                {key:'product_reviews_enabled', label:'Product Reviews',     sub:'Buyers can rate products'},
                                                {key:'shop_reviews_enabled',    label:'Shop Reviews',        sub:'Buyers can rate shops'},
                                                {key:'personalisation_enabled', label:'Personalisation',     sub:'Personalised home sections'},
                                                {key:'promotions_enabled',      label:'Promotions System',   sub:'Seller promotion entry points'},
                                            ].map(f => (
                                                <FieldRow key={f.key} label={f.label} sub={f.sub}>
                                                    <Switch
                                                        checked={appSettings[f.key] !== false}
                                                        onChange={v => saveAppSettings({[f.key]: v})}
                                                    />
                                                </FieldRow>
                                            ))}
                                        </Section>
                                    </MDBCol>
                                    <MDBCol md="6">
                                        <Section title="Product Price Limits"
                                                 sub="Set min/max listing price in MWK. 0 = no limit">
                                            <FieldRow label="Min price (MWK)" sub="0 = no minimum">
                                                <NumField
                                                    value={appSettings.product_min_price ?? 0}
                                                    prefix="MWK"
                                                    onSave={v => saveAppSettings({product_min_price: v})}
                                                />
                                            </FieldRow>
                                            <FieldRow label="Max price (MWK)" sub="0 = no maximum">
                                                <NumField
                                                    value={appSettings.product_max_price ?? 0}
                                                    prefix="MWK"
                                                    onSave={v => saveAppSettings({product_max_price: v})}
                                                />
                                            </FieldRow>
                                        </Section>

                                        <Section title="OTP / Auth"
                                                 sub="Controls which OTP provider is used for phone authentication">
                                            <FieldRow label="Africa's Talking OTP" sub="Primary OTP provider">
                                                <Switch
                                                    checked={appSettings.otp_at_enabled !== false}
                                                    onChange={v => saveAppSettings({otp_at_enabled: v})}
                                                />
                                            </FieldRow>
                                            <FieldRow label="Firebase fallback OTP"
                                                      sub="Falls back if AT fails. Keep on unless there's an issue.">
                                                <Switch
                                                    checked={appSettings.otp_firebase_fallback_enabled !== false}
                                                    onChange={v => saveAppSettings({otp_firebase_fallback_enabled: v})}
                                                />
                                            </FieldRow>
                                        </Section>
                                    </MDBCol>
                                </MDBRow>
                            </TabPane>

                            {/* ── Notifications ── */}
                            <TabPane tab="Notifications" key="notifications">
                                <MDBRow>
                                    <MDBCol md="6">
                                        <Section title="Seller Notifications"
                                                 sub="Push always fires. These flags control SMS (phone sellers) and email (Gmail sellers) only.">
                                            {[
                                                {key:'seller_notify_sms_new_order',          label:'SMS — New order',        sub:'Costs per SMS. Off by default.'},
                                                {key:'seller_notify_email_new_order',         label:'Email — New order',      sub:'Gmail sellers only'},
                                                {key:'seller_notify_email_order_cancelled',   label:'Email — Order cancelled',sub:'Gmail sellers only'},
                                                {key:'seller_notify_email_payout_sent',       label:'Email — Payout sent',    sub:'Gmail sellers only'},
                                            ].map(n => (
                                                <FieldRow key={n.key} label={n.label} sub={n.sub}>
                                                    <Switch
                                                        checked={notifSettings[n.key] !== false}
                                                        onChange={v => saveNotifSettings({[n.key]: v})}
                                                        checkedChildren="ON"
                                                        unCheckedChildren="OFF"
                                                    />
                                                </FieldRow>
                                            ))}
                                        </Section>
                                    </MDBCol>
                                    <MDBCol md="6">
                                        <Section title="Buyer Notifications"
                                                 sub="Notifications sent to buyers when their order is affected.">
                                            {[
                                                {key:'buyer_notify_sms_order_cancelled_by_seller',  label:'SMS — Order cancelled by seller',  sub:'Costs per SMS. Off by default.'},
                                                {key:'buyer_notify_email_order_cancelled_by_seller', label:'Email — Order cancelled by seller', sub:'Gmail buyers only'},
                                                {key:'buyer_notify_sms_refund_paid',                label:'SMS — Refund dispatched',           sub:'Costs per SMS. Off by default.'},
                                                {key:'buyer_notify_email_refund_paid',              label:'Email — Refund dispatched',         sub:'Gmail buyers only'},
                                            ].map(n => (
                                                <FieldRow key={n.key} label={n.label} sub={n.sub}>
                                                    <Switch
                                                        checked={notifSettings[n.key] !== false}
                                                        onChange={v => saveNotifSettings({[n.key]: v})}
                                                        checkedChildren="ON"
                                                        unCheckedChildren="OFF"
                                                    />
                                                </FieldRow>
                                            ))}
                                        </Section>

                                        <div style={{background:'#fef3c7', borderRadius:10, padding:'12px 16px',
                                                     fontSize:12, color:'#92400e', border:'1px solid #fde68a'}}>
                                            <b>SMS cost note:</b> Africa's Talking charges per SMS sent.
                                            Enable SMS flags only when you're ready to absorb the cost.
                                            Email notifications via Resend have a generous free tier.
                                        </div>
                                    </MDBCol>
                                </MDBRow>
                            </TabPane>

                        </Tabs>
                    </div>
                </Content>
            </Layout>
        </Layout>
    );
};

export default Settings;
